import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import SimpleCard from "connectui/component/display/card/SimpleCard";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Text from "connectui/component/display/Text";
import Container from "connectui/component/layout/Container";
import ScrollableContainer from "connectui/component/layout/ScrollableContainer";

import EmptyButtonBase from "connectui/component/input/button/base/EmptyButtonBase";
import ButtonIconText from "connectui/component/input/button/ButtonIconText";
import Tooltip from "connectui/component/display/Tooltip";
import ChipIconText from "connectui/component/display/chip/ChipIconText";
import SvgIcon from "connectui/component/display/SvgIcon";
import ICONS from "connectui/icons/iconMap";
import {DonutChart} from "connectui/component/charts";
import ModalBase from "connectui/component/modal/ModalBase";
import {getDashboardBPStats} from "redux/actions/dashboardActions";
import {buildStyles} from "connectui/component/buildStyles";
import StatusIcon from "../../../../connectui/component/display/StatusIcon";
import DashboardEmptyStateCard from "./DashboardEmptyStateCard";
import {useTranslation} from "react-i18next";
import Divider from "../../../../connectui/component/display/Divider";

const useStyles = (theme) =>
  buildStyles({
    truncatedText: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      width: "100%",
      textAlign: "left",
    },
  });

const BoardingPassVerificationCard = ({timeframe, listingGroups = null}) => {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const bpData = useSelector((state) => state.dashboardReducer.bpData);
  const {t} = useTranslation();

  useEffect(() => {
    if (!timeframe) return;
    setLoading(true);
    setError(false);
    let isMounted = true;
    dispatch(
      getDashboardBPStats({
        start_date: timeframe.startDate,
        end_date: timeframe.endDate,
        listing_group_ids: listingGroups,
        onSuccess: () => {
          if (isMounted) setLoading(false);
        },
        onError: () => {
          if (isMounted) {
            setLoading(false);
            setError(true);
          }
        },
      }),
    );
    return () => {
      isMounted = false;
    };
  }, [timeframe?.startDate, timeframe?.endDate, listingGroups, dispatch]);

  const boardingPassOpenedCount =
    bpData?.flow_pairs
      ?.filter((flow) => flow.from === "Boarding Pass Opened")
      .reduce((sum, flow) => sum + flow.count, 0) || 0;

  const chartData = React.useMemo(() => {
    if (!bpData?.flow_pairs) return [];
    const flowGroups = {};
    bpData.flow_pairs.forEach((flow) => {
      if (flow.from === "Boarding Pass Opened") return;
      if (!flowGroups[flow.from]) {
        flowGroups[flow.from] = {
          id: flow.from,
          display: flow.from,
          value: 0,
          color: flow.from_color,
        };
      }
      flowGroups[flow.from].value += flow.count;
    });
    return Object.values(flowGroups).sort((a, b) => b.value - a.value);
  }, [bpData]);

  const showEmpty =
    !loading &&
    (!chartData.length || chartData.every((item) => item.value === 0));

  const renderBoardingPassList = ({maxItems = null, isModal = false}) => {
    const displayedItems = maxItems ? chartData.slice(0, maxItems) : chartData;

    // Hacky fix sorry
    let containerHeight;
    if (isModal) {
      containerHeight = isMobile ? "200px" : "400px";
    } else {
      containerHeight = "180px";
    }

    return (
      <Container
        sx={{
          paddingRight: isMobile ? 0 : "240px",
          marginTop: isMobile ? 0 : theme.spacing(6),

          width: "100%",
        }}
      >
        <ScrollableContainer height={containerHeight} sx={{width: "100%"}}>
          <FlexContainer
            flexDirection="column"
            gap={theme.spacing(4)}
            sx={{width: "100%"}}
          >
            {displayedItems.map((item) => (
              <FlexContainer
                key={item.id}
                width="100%"
                justifyContent="space-between"
                alignItems="center"
                sx={{minWidth: 0}}
              >
                <FlexContainer
                  alignItems="center"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    marginRight: theme.spacing(2),
                    overflow: "hidden",
                  }}
                >
                  <StatusIcon color={item.color} sx={{flexShrink: 0}} />
                  <Tooltip
                    tooltipContent={item.display}
                    sx={{
                      trigger: {
                        flex: 1,
                        minWidth: 0,
                        overflow: "hidden",
                        marginLeft: theme.spacing(1),
                      },
                    }}
                  >
                    <Text
                      variant="textSM"
                      color="textSecondary"
                      className={classes.truncatedText}
                      sx={{width: "100%", minWidth: 0}}
                    >
                      {item.display}
                    </Text>
                  </Tooltip>
                </FlexContainer>
                <ChipIconText
                  text={`${item.value} Guests`}
                  icon={
                    <SvgIcon
                      component={ICONS.user}
                      sx={{
                        color: item.color,
                        width: "14px",
                        height: "14px",
                      }}
                    />
                  }
                  typographyVariant="textSM"
                  weight="medium"
                  textColor="textPrimary"
                  sx={{
                    backgroundColor: `${item.color}20`,
                    padding: theme.spacing(0.5, 2),
                    flexShrink: 0,
                    minWidth: "max-content",
                  }}
                />
              </FlexContainer>
            ))}
          </FlexContainer>
        </ScrollableContainer>
      </Container>
    );
  };

  const renderBottomBar = () => (
    <FlexContainer
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      sx={{width: "100%"}}
    >
      <Divider />
      <ButtonIconText
        buttonBase={EmptyButtonBase}
        label="View More"
        onClick={() => setModalOpen(true)}
        color={theme.colors.primary}
        as="div"
        sx={{
          padding: theme.spacing(1, 2),
          color: theme.colors.primary,
          fontSize: theme.typography.textSM.fontSize,
          fontWeight: theme.typography.textSM.medium.fontWeight,
          "&:hover": {
            backgroundColor: theme.colors.primary50,
          },
        }}
        icon={
          <SvgIcon
            component={ICONS["arrow-up-right"]}
            sx={{
              width: "20px",
              height: "20px",
              marginRight: theme.spacing(0.5),
            }}
          />
        }
      />
    </FlexContainer>
  );

  const renderCardTitle = () => (
    <FlexContainer alignItems="center" gap={theme.spacing(1)}>
      <Text variant="textMD" color="textSecondary" weight="medium">
        {t("dashboard.boarding-pass-verification.title")}
      </Text>
      <Tooltip tooltipContent={t("dashboard.boarding-pass-verification.help")}>
        <Container sx={{cursor: "help"}}>
          <SvgIcon
            component={ICONS["help-circle"]}
            sx={{
              width: "14px",
              height: "14px",
              color: theme.colors.gray400,
            }}
          />
        </Container>
      </Tooltip>
    </FlexContainer>
  );

  return (
    <>
      <SimpleCard
        customContent={
          <Container sx={{position: "relative", width: "100%", height: "100%"}}>
            {renderCardTitle()}

            {loading && !error && (
              <>
                <Container
                  skeleton={{
                    enabled: true,
                    width: "220px",
                    height: "220px",
                    style: {borderRadius: "50%"},
                  }}
                  sx={{
                    position: isMobile ? "static" : "absolute",
                    top: isMobile ? "auto" : 0,
                    right: isMobile ? "auto" : 0,
                    width: "220px",
                    height: "220px",
                    zIndex: 2,
                    margin: isMobile ? "0 auto" : "unset",
                  }}
                />
                <Container
                  sx={{
                    paddingRight: isMobile ? 0 : "240px",
                    marginTop: isMobile ? theme.spacing(2) : 0,
                    width: "100%",
                  }}
                >
                  <ScrollableContainer height="180px" sx={{width: "100%"}}>
                    <FlexContainer
                      flexDirection="column"
                      gap={theme.spacing(4)}
                      sx={{width: "100%"}}
                    >
                      {Array.from({length: 4}).map((_, i) => (
                        <Container
                          key={i}
                          skeleton={{
                            enabled: true,
                            width: "100%",
                            height: "32px",
                            style: {borderRadius: "8px"},
                          }}
                          sx={{marginBottom: theme.spacing(1)}}
                        />
                      ))}
                    </FlexContainer>
                  </ScrollableContainer>
                </Container>
              </>
            )}

            {(showEmpty || error) && !loading && (
              <DashboardEmptyStateCard
                text={t("dashboard.boarding-pass-verification.empty-state")}
                icon={ICONS["no-messages-white"]}
                height="300px"
              />
            )}

            {!showEmpty && !loading && !error && (
              <>
                <Container
                  sx={{
                    position: isMobile ? "static" : "absolute",
                    top: isMobile ? "auto" : 0,
                    right: isMobile ? "auto" : 0,
                    width: "220px",
                    height: "220px",
                    zIndex: 2,
                    margin: isMobile ? "0 auto" : "unset",
                  }}
                >
                  <DonutChart
                    data={chartData}
                    settings={{
                      innerRadius: 0.7,
                      outerRadius: 1.1,
                      centerContent: {
                        element: (
                          <FlexContainer
                            flexDirection="column"
                            alignItems="center"
                            gap={theme.spacing(0.5)}
                          >
                            <Text
                              variant="textSM"
                              weight="medium"
                              color="textPrimary"
                            >
                              {boardingPassOpenedCount} Guests
                            </Text>
                            <Text variant="textXS" color="textSecondary">
                              Boarding Pass Opened
                            </Text>
                          </FlexContainer>
                        ),
                      },
                    }}
                  />
                </Container>

                {renderBoardingPassList({maxItems: 4})}

                {chartData.length > 4 && renderBottomBar()}
              </>
            )}
          </Container>
        }
        sx={{
          height: "100%",
          width: "100%",
          padding: theme.spacing(5, 5, 3, 5),
          backgroundColor: theme.colors.white,
        }}
      />

      <ModalBase
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        sx={{
          padding: theme.spacing(6),
          maxWidth: "800px",
          width: "90%",
          maxHeight: "80vh",
        }}
      >
        <FlexContainer
          flexDirection="column"
          gap={theme.spacing(3)}
          sx={{height: "100%"}}
        >
          <FlexContainer
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            {renderCardTitle()}
            <SvgIcon
              component={ICONS.x}
              onClick={() => setModalOpen(false)}
              sx={{
                cursor: "pointer",
                width: "14px",
                height: "14px",
                color: theme.colors.gray500,
              }}
            />
          </FlexContainer>

          <Container sx={{position: "relative", width: "100%", flex: 1}}>
            <Container
              sx={{
                position: isMobile ? "static" : "absolute",
                top: isMobile ? "auto" : "50%",
                right: isMobile ? "auto" : 0,
                transform: isMobile ? "none" : "translateY(-50%)",
                width: "220px",
                height: "220px",
                zIndex: 2,
                margin: isMobile ? "0 auto" : "unset",
              }}
            >
              <DonutChart
                data={chartData}
                settings={{
                  innerRadius: 0.7,
                  outerRadius: 1.1,
                  centerContent: {
                    element: (
                      <FlexContainer
                        flexDirection="column"
                        alignItems="center"
                        gap={theme.spacing(0.5)}
                      >
                        <Text
                          variant="textSM"
                          weight="medium"
                          color="textPrimary"
                        >
                          {boardingPassOpenedCount} Guests
                        </Text>
                        <Text variant="textXS" color="textSecondary">
                          Boarding Pass Opened
                        </Text>
                      </FlexContainer>
                    ),
                  },
                }}
              />
            </Container>

            {renderBoardingPassList({isModal: true})}
          </Container>
        </FlexContainer>
      </ModalBase>
    </>
  );
};

export default BoardingPassVerificationCard;
